import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';

import { Button, Card } from '@/design-system';
import type { Payment } from '@/entities/credit';

import { billingCopy } from '../copy';
import { describeBillingError } from '../errors';
import { refreshAfterPayment, usePaymentStatus } from '../hooks';

export interface PaymentReturnCardProps {
    /** De betaling uit de terugkeerlink (`?betaling=`); `null` als die er niet (geldig) in stond. */
    paymentUuid: string | null;
    onClose: () => void;
}

/**
 * Terug van de betaalpagina: hoe staat de betaling ervoor?
 *
 * De terugkeer zelf zegt niets - ook wie de betaling afbreekt of wiens betaling
 * mislukt, komt hier terug. Het scherm vraagt het aan de server
 * (`GET /credits/payments/{uuid}`) en zegt wat daar staat: geslaagd (het
 * abonnement loopt, of de keuringen zijn bijgeboekt), mislukt, afgebroken,
 * verlopen, of nog in behandeling. Zodra de betaling definitief is, laden het
 * abonnement, het tegoed en de betalingen opnieuw.
 */
export function PaymentReturnCard({ paymentUuid, onClose }: PaymentReturnCardProps) {
    const client = useQueryClient();
    const { payment, gaveUp } = usePaymentStatus(paymentUuid);
    const current = payment.data ?? null;
    const status = current?.status ?? null;

    useEffect(() => {
        if (status !== null && status !== 'open') {
            refreshAfterPayment(client);
        }
    }, [client, status]);

    if (paymentUuid === null) {
        return (
            <Notice onClose={onClose}>
                <Text>{billingCopy.returnUnknown}</Text>
            </Notice>
        );
    }

    if (payment.isError) {
        return (
            <Notice
                variant="warn"
                onClose={onClose}
                onCheckAgain={() => void payment.refetch()}
                checking={payment.isFetching}
            >
                <Text warn>{describeBillingError(payment.error)}</Text>
            </Notice>
        );
    }

    if (current === null) {
        return (
            <Notice onClose={onClose}>
                <Text>{billingCopy.returnChecking}</Text>
            </Notice>
        );
    }

    const subscription =
        current.purpose === 'subscription' || current.purpose === 'subscription_renewal';

    switch (current.status) {
        case 'paid':
            return (
                <Notice variant="accent" title={billingCopy.returnPaidTitle} onClose={onClose}>
                    <Text>
                        {paidText(current)} {billingCopy.returnInvoiceFollows}
                    </Text>
                </Notice>
            );
        case 'failed':
        case 'cancelled':
        case 'expired':
            return (
                <Notice
                    variant="warn"
                    title={
                        current.status === 'failed'
                            ? billingCopy.returnFailedTitle
                            : current.status === 'cancelled'
                              ? billingCopy.returnCancelledTitle
                              : billingCopy.returnExpiredTitle
                    }
                    onClose={onClose}
                >
                    <Text warn>{billingCopy.returnNothingHappened(subscription)}</Text>
                </Notice>
            );
        default:
            return (
                <Notice
                    variant={gaveUp ? 'warn' : 'default'}
                    title={billingCopy.returnOpenTitle}
                    onClose={onClose}
                    onCheckAgain={gaveUp ? () => void payment.refetch() : undefined}
                    checking={payment.isFetching}
                >
                    <Text warn={gaveUp}>
                        {gaveUp ? billingCopy.returnProcessing : billingCopy.returnOpen}
                    </Text>
                </Notice>
            );
    }
}

function paidText(payment: Payment): string {
    switch (payment.purpose) {
        case 'subscription':
            return billingCopy.returnPaidSubscription(payment.planName, payment.credits);
        case 'extra_inspections':
        case 'single_reports':
            return billingCopy.returnPaidInspections(payment.quantity ?? payment.credits);
        default:
            return billingCopy.returnPaidOther;
    }
}

interface NoticeProps {
    variant?: 'default' | 'accent' | 'warn';
    /** De uitkomst in twee of drie woorden: "Betaling geslaagd", "Betaling mislukt". */
    title?: string;
    onClose: () => void;
    onCheckAgain?: () => void;
    checking?: boolean;
    children: ReactNode;
}

function Notice({
    variant = 'default',
    title,
    onClose,
    onCheckAgain,
    checking = false,
    children,
}: NoticeProps) {
    return (
        <Card variant={variant} padding="md" role="status" aria-live="polite">
            {title ? (
                <p
                    className={
                        variant === 'warn'
                            ? 'm-0 mb-1 text-[14px] leading-[1.4] font-semibold text-[var(--warn-ink)]'
                            : 'm-0 mb-1 text-[14px] leading-[1.4] font-semibold text-[var(--text-primary)]'
                    }
                >
                    {title}
                </p>
            ) : null}
            {children}
            <div className="mt-3 flex flex-wrap gap-2">
                {onCheckAgain ? (
                    <Button variant="outline" size="md" disabled={checking} onClick={onCheckAgain}>
                        {billingCopy.checkAgain}
                    </Button>
                ) : null}
                <Button variant="outline" size="md" onClick={onClose}>
                    {billingCopy.close}
                </Button>
            </div>
        </Card>
    );
}

function Text({ warn = false, children }: { warn?: boolean; children: ReactNode }) {
    return (
        <p
            className={
                warn
                    ? 'm-0 text-[14px] leading-[1.5] text-[var(--warn-ink)]'
                    : 'm-0 text-[14px] leading-[1.5] text-[var(--text-primary)]'
            }
        >
            {children}
        </p>
    );
}
